import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';

import { BaseModal, Button, Select, Textarea } from 'src/components';
import { cancelSubscription } from 'src/api/functions/subscriptionApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import useModal from 'src/hooks/useModal';
import { TSubscription } from 'src/types/TSubscription';
import messages from './messages';
import styles from './styles.module.scss';

// Fixed reason list — MUST match the backend CANCELLATION_REASONS
// (filespin/core/subscription.py). `other` requires the free-text note.
const REASON_MESSAGE_KEYS: Array<{ value: string; message: keyof typeof messages }> = [
  { value: 'too_expensive', message: 'cancelReasonTooExpensive' },
  { value: 'missing_features', message: 'cancelReasonMissingFeatures' },
  { value: 'switching_provider', message: 'cancelReasonSwitchingProvider' },
  { value: 'not_using', message: 'cancelReasonNotUsing' },
  { value: 'temporary', message: 'cancelReasonTemporary' },
  { value: 'other', message: 'cancelReasonOther' },
];

/**
 * Cancel-with-reason modal (Phase D+). Cancellation is AT PERIOD END — the
 * subscription keeps access until `current_period_end`. On confirm we POST the
 * reason to /api/v1/billing/cancel and refresh the subscription query so the
 * page reflects the cancelled state (+ reactivate option) without a reload.
 *
 * Rendered via `useModal().showModal(<CancelSubscriptionModal .../>)`.
 */
const CancelSubscriptionModal: React.FC<{ subscription: TSubscription }> = () => {
  const intl = useIntl();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const options = useMemo(
    () => [
      { value: '', label: intl.formatMessage(messages.cancelReasonPlaceholder) },
      ...REASON_MESSAGE_KEYS.map(({ value, message }) => ({
        value,
        label: intl.formatMessage(messages[message]),
      })),
    ],
    [intl],
  );

  const isOther = reasonCode === 'other';
  const canConfirm =
    !!reasonCode && (!isOther || reasonText.trim().length > 0) && !submitting;

  const onConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(false);
    try {
      const result = await cancelSubscription(
        reasonCode,
        reasonText.trim() || null,
        accessToken,
      );
      if (isErrorResponseResult(result)) {
        setError(true);
        return;
      }
      await queryClient.invalidateQueries([queryNames.subscription]);
      closeModal();
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal>
      <div className={styles.cancelModal}>
        <h3 className={styles.cancelModalTitle}>
          <FormattedMessage {...messages.cancelModalTitle} />
        </h3>
        <p className={styles.cancelModalIntro}>
          <FormattedMessage {...messages.cancelModalIntro} />
        </p>

        <Select
          label={intl.formatMessage(messages.cancelReasonLabel)}
          value={reasonCode}
          onChange={setReasonCode}
          options={options}
        />

        <label className={styles.cancelDetailsLabel}>
          <FormattedMessage
            {...(isOther
              ? messages.cancelDetailsLabelRequired
              : messages.cancelDetailsLabel)}
          />
        </label>
        <Textarea
          value={reasonText}
          onChange={setReasonText}
          placeholder={intl.formatMessage(messages.cancelDetailsPlaceholder)}
        />

        {error && (
          <p className={styles.checkoutError} role="alert">
            <FormattedMessage {...messages.cancelModalError} />
          </p>
        )}

        <div className={styles.cancelModalActions}>
          <Button mode="secondary" size="small" onClick={closeModal} disabled={submitting}>
            <FormattedMessage {...messages.cancelModalKeepPlan} />
          </Button>
          <Button
            mode="decline"
            size="small"
            onClick={onConfirm}
            disabled={!canConfirm}
            loading={submitting}
          >
            <FormattedMessage
              {...(submitting ? messages.cancelModalConfirming : messages.cancelModalConfirm)}
            />
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default CancelSubscriptionModal;
